import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import ReactToPrint from 'react-to-print';
import { Box, Button, Typography } from '@mui/material';

import GenericDialog from '../components/GenericDialog';
import ContentBox from '../components/layout/ContentBox';
import DetailedEvaluation from '../components/survey/evaluation/DetailedEvaluation';
import EvaluationOverview from '../components/survey/evaluation/EvaluationOverview';
import Review from '../components/review/Review';
import EvaluationTemplate from '../components/print/evaluation/EvaluationTemplate';
import MultiReviewComments from '../components/review/reviewCategory/textblockReview/MultiReviewComments';

import {
  Enrollment,
  Certificate,
  Submission,
  Invoice,
  ThirdPartyEnrollment,
  QuipUser,
  Institute,
  Trial,
  Survey,
  ThirdPartyTrial,
  ReviewRequest,
  Review as ReviewType,
  DetailedEvaluation as DetailedEvaluationType,
  SurveySubmissionMeta,
} from '../redux/types';
import { AuswertungSetting } from '../redux/types/auswertungSettingTypes';

export type ExtendedEnrollment = Enrollment & {
  certificate?: Certificate;
  submission?: Submission & {
    review: ReviewType & {
      reviewRequest: ReviewRequest;
    };
  };
  surveySubmissionMeta?: SurveySubmissionMeta;
  invoices: Invoice[];
  thirdPartyEnrollment?: ThirdPartyEnrollment;
  quipUser: QuipUser & {
    institute: Institute;
  };
};

type ExtendedTrial = Trial & {
  survey?: Survey;
  thirdPartyTrial?: ThirdPartyTrial;
};

type Props = {
  enrollment: ExtendedEnrollment;
  trial: ExtendedTrial;
  evaluation: DetailedEvaluationType;
  auswertungSetting?: AuswertungSetting;
  onClose: () => void;
}

type State = {
  pointIndex: number;
}

export default class EvaluationDialog extends Component<Props, State> {
  componentRef: any;

  constructor(props: Props) {
    super(props);
    this.state = { pointIndex: 0 };
  }

  setPointIndex = (index: number): void => {
    this.setState({ pointIndex: index });
  };

  /**
   * provide the IDs of the questions that are excluded from the survey.
   * i.e. In Groundtruth berücksichtigen is unchecked.
   * Questions with these ids can be excluded from the Auswertung.
   */
  static getExcludedQuestions = (survey: Survey): string[][] => {
    const surveySections = survey.payload.sections;
    const excludedSectionQuestionIds = surveySections
      .map((section) => section.questions.filter((question) => (
        question.excludeFromMarking && question.excludeFromMarking === true
      )).map((questions) => questions.question.uuid));
    return excludedSectionQuestionIds;
  };

  render() {
    const {
      enrollment, trial, evaluation, onClose, auswertungSetting,
    } = this.props;
    const { pointIndex } = this.state;
    const { submission } = enrollment;
    const review = submission?.review;
    const reviewRequest = review?.reviewRequest;
    const reviewSurvey = reviewRequest?.reviewSurvey;
    const { survey } = trial;
    if (!survey) {
      return null;
    }
    const excludedQuestions = EvaluationDialog.getExcludedQuestions(survey);

    let showReviewComments = false;
    if (review) {
      showReviewComments = (!!review.sectionJustifications && !!review.sectionJustifications[0])
        || !!review.justification || !!review.extendedPayload;
    }
    return (
      <GenericDialog
        isOpen={!!evaluation}
        onClose={onClose}
        closeButtonLabel={<FormattedMessage id="genericClose" />}
        maxWidth="lg"
        actions={[
          <ReactToPrint
            key={1}
            trigger={() => (
              <Button
                variant="contained"
                color="primary"
              >
                <FormattedMessage id="enrollmentsmanager.detailedEvaluationPrintButton" />
              </Button>
            )}
            content={() => this.componentRef}
          />,
        ]}
      >
        <div id="DetailEvaluationContent" style={{ margin: '0.5em' }}>
          <ContentBox
            elevation={0}
            title={(
              <FormattedMessage
                id="enrollmentsmanager.detailedEvaluation"
                values={{ trial: trial.name }}
              />
            )}
          >
            <EvaluationOverview
              evaluationOverview={evaluation.overview}
              points={evaluation.points}
              pointIndex={pointIndex}
              hideIndividualScores={trial.hideIndividualScores || !evaluation.points}
              evaluationType={trial.survey?.evaluationType}
              isFaerbungReview={!!reviewSurvey}
              auswertungSetting={auswertungSetting}
            />
          </ContentBox>
          <DetailedEvaluation
            evaluation={evaluation.sectionEvaluations}
            onTabClicked={this.setPointIndex}
            currentSection={pointIndex}
            excludedQuestions={excludedQuestions}
          />
          {
            review
            && survey.payload
            && enrollment.certificate?.evaluation
            && submission
            && (
              <div>
                <ContentBox elevation={0} title={<FormattedMessage id="trialdetails.reviewTitle" />}>
                  <Review
                    survey={survey?.payload}
                    reviewSurvey={reviewSurvey}
                    reviewSubmission={review}
                    currentSection={pointIndex}
                    onCurrentSectionChange={this.setPointIndex}
                    evaluation={enrollment.certificate.evaluation}
                    submission={submission}
                    excludedQuestions={excludedQuestions}
                    readOnly
                    disableReview
                  />
                </ContentBox>
                <ContentBox
                  elevation={0}
                  title={<FormattedMessage id="evaluationdialog.reviewsection" />}
                  show={showReviewComments}
                >
                  {
                    review.sectionJustifications
                    && review.sectionJustifications[0] && (
                      <Box>
                        <Typography variant="h5" fontWeight="bold"><FormattedMessage id="trialdetails.sectionReview" /></Typography>
                        <Typography>{review.sectionJustifications[pointIndex]}</Typography>
                      </Box>
                    )
                  }
                  {
                    review.extendedPayload && review.extendedPayload.sections
                    && review.extendedPayload.sections[pointIndex]
                    && (
                      <MultiReviewComments
                        sectionComments={review.extendedPayload.sections[pointIndex]}
                      />
                    )
                  }
                  {
                    review.justification && (
                      <Box>
                        <Typography variant="h5" fontWeight="bold"><FormattedMessage id="trialdetails.sectionOverview" /></Typography>
                        <Typography>{review.justification}</Typography>
                      </Box>
                    )
                  }
                </ContentBox>
              </div>
            )
          }
        </div>
        <div style={{ display: 'none' }}>
          <EvaluationTemplate
            enrollment={enrollment}
            trial={trial}
            evaluation={evaluation}
            excludedQuestions={excludedQuestions}
            showReviewComments={showReviewComments}
            ref={(el) => { this.componentRef = el; }}
          />
        </div>
      </GenericDialog>
    );
  }
}
